import { filterActionableConnections, isFileDatasourceType } from 'DataSources/datasourcesApi';
import { dropUnmaskableFiles } from 'Secure/MaskingSources/fileInventoryUtils';

/** Mask-eligible, credentialed connections — the candidate sources for the manager list.
 * mongodb is included: mongo masking is supported (mongodb→mongodb). dynamodb is not —
 * no NoSQL masking for dynamo yet; platform rejects with ENGINE_NOT_MASKABLE. */
export function maskEligibleConnections(connections) {
  return filterActionableConnections(connections)
    .filter((c) => (c.type || '').toLowerCase() !== 'dynamodb');
}

/** Friendly platform labels for file sources, keyed by the connection's vfsScheme. */
const VFS_SCHEME_LABELS = {
  S3: 's3',
  AZURE_BLOB: 'azure',
  SFTP: 'sftp',
  SHAREPOINT: 'sharepoint',
  LOCAL: 'local',
};

/** "s3" / "azure" / "sharepoint" / … from vfsScheme, falling back to the rootPath's
 * scheme prefix (azblob:// → azure), then "local". */
function fileSchemeLabel(s) {
  const scheme = s?.vfsScheme;
  if (scheme && VFS_SCHEME_LABELS[scheme]) return VFS_SCHEME_LABELS[scheme];
  const m = String(s?.rootPath || '').match(/^([a-z][a-z0-9+]*):\/\//i);
  if (m) {
    const raw = m[1].toLowerCase();
    return raw === 'azblob' ? 'azure' : raw;
  }
  return 'local';
}

/** Compress a rootPath to its last two elements: "/data/exports/2026" → ".../exports/2026".
 * Paths of two or fewer elements are shown as-is (minus any scheme prefix). */
function shortenRootPath(rootPath) {
  const raw = String(rootPath || '');
  if (!raw) return '';
  const stripped = raw.replace(/^[a-z][a-z0-9+]*:\/\//i, '');
  const parts = stripped.split('/').filter(Boolean);
  if (parts.length <= 2) return stripped;
  return `.../${parts.slice(-2).join('/')}`;
}

/** Subtitle under a source's name. JDBC sources show their engine; file sources show the
 * platform plus a compressed path ("s3 · .../exports/2026") — pair with
 * `sourceSubtitleTitle` on the element's title so hovering reveals the whole path. */
export function sourceSubtitle(s) {
  if (!isFileDatasourceType(s?.datasourceType)) return s?.type;
  const label = fileSchemeLabel(s);
  const short = shortenRootPath(s.rootPath);
  return short ? `${label} · ${short}` : label || s?.type;
}

/** Hover text for the subtitle — the FULL rootPath for file sources. */
export function sourceSubtitleTitle(s) {
  return isFileDatasourceType(s?.datasourceType) ? (s.rootPath || s.type) : s?.type;
}

/**
 * For file sources, drop inventory entries (table = file) whose format masking doesn't
 * support — they can't be masked, so the manager doesn't show or persist them. Maskability
 * keys on each file's content-sniffed format from the discovery results (`tableMeta`),
 * falling back to the extension heuristic for runs without sampler_type. JDBC sources
 * pass through untouched.
 */
export function filterMaskableFiles(selectedFields, source, tableMeta) {
  if (!isFileDatasourceType(source?.datasourceType)) return selectedFields;
  return dropUnmaskableFiles(selectedFields, source.datasourceType, tableMeta);
}

/** Display label for an inventory's masking provider — same resolution as ProviderBadge. */
export function providerLabelFor(inventory, providers, defaultLabel = 'C2 Masking') {
  const list = Array.isArray(providers) ? providers : [];
  const id = inventory && inventory.preferredMaskingProviderId;
  const selected =
    (id && list.find((p) => p.serviceId === id)) ||
    list.find((p) => p.isDefault) ||
    list[0] ||
    null;
  if (!selected) return defaultLabel;
  return selected.vendor ? (selected.vendorDisplayName || selected.name) : defaultLabel;
}
